import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

/**
 * Hook to automatically sync Stripe subscription when user logs in
 * This ensures database is always in sync with Stripe
 */
export const useSyncStripeSubscription = () => {
  const { user, session } = useAuth();
  const hasSyncedRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // Only sync if user is authenticated and Supabase is configured
    if (!user || !session || !isSupabaseConfigured || !supabase) {
      hasSyncedRef.current = false;
      lastUserIdRef.current = null;
      return;
    }

    // Only sync once per user session, or if user changed
    const currentUserId = user.id;
    if (hasSyncedRef.current && lastUserIdRef.current === currentUserId) {
      return;
    }

    // Mark as syncing to prevent duplicate calls
    hasSyncedRef.current = true;
    lastUserIdRef.current = currentUserId;

    // Sync subscription from Stripe
    const syncSubscription = async () => {
      try {
        console.log('🔄 [useSyncStripeSubscription] Syncing subscription for user:', currentUserId);
        
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession?.access_token) {
          console.warn('⚠️ [useSyncStripeSubscription] No access token available');
          return;
        }

        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-stripe-subscription`;
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${currentSession.access_token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          let errorData: any = {};
          try {
            const text = await response.text();
            errorData = text ? JSON.parse(text) : {};
          } catch (e) {
            console.error('Failed to parse error response:', e);
          }
          
          // ✅ Analyser le type d'erreur pour déterminer si on doit retry
          const isNetworkError = response.status === 0 || response.status >= 500;
          const isAuthError = response.status === 401 || response.status === 403;
          
          console.error('❌ [useSyncStripeSubscription] Sync failed:', {
            status: response.status,
            statusText: response.statusText,
            error: errorData.error || 'Unknown error',
            details: errorData.details || 'No details available',
            isNetworkError,
            isAuthError,
            fullResponse: errorData
          });
          
          // ✅ Pour les erreurs réseau, on pourrait retry plus tard
          // Pour les erreurs d'auth, ne pas retry (session expirée)
          if (isAuthError) {
            console.warn('⚠️ [useSyncStripeSubscription] Auth error, not retrying');
          }
          
          // Don't throw - this is a background sync, failures are acceptable
          return;
        }

        const result = await response.json();
        if (result.success) {
          console.log('✅ [useSyncStripeSubscription] Subscription synced successfully:', result.subscription);
          
          // Dispatch event to notify other components
          window.dispatchEvent(new CustomEvent('ff:subscription:synced', {
            detail: { subscription: result.subscription }
          }));
        }
      } catch (error) {
        // Non-critical error - just log it
        console.warn('⚠️ [useSyncStripeSubscription] Error syncing subscription (non-critical):', error);
      }
    };

    // Debounce sync to avoid multiple calls
    const timeoutId = setTimeout(() => {
      syncSubscription();
    }, 1000); // Wait 1 second after login to ensure everything is ready

    return () => {
      clearTimeout(timeoutId);
    };
  }, [user?.id, session?.access_token]);

  // Also listen for session refresh events
  useEffect(() => {
    if (!user || !isSupabaseConfigured) return;

    const handleSessionRefresh = async () => {
      // Reset sync flag to allow re-sync on session refresh
      hasSyncedRef.current = false;
    };

    window.addEventListener('ff:session:refreshed', handleSessionRefresh);
    return () => {
      window.removeEventListener('ff:session:refreshed', handleSessionRefresh);
    };
  }, [user?.id]);
};

